var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { AnimatePresence, motion } from "framer";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { ExternalIcon } from "../../assets/icons/icons";
import { BaseLink, SecondaryText } from "../../designSystem";
import sizes from "../../designSystem/sizes";
import useElementSize from "../../hooks/useElementSize";
import Pagination from "./Pagination";
var logoContainerWidth = 40;
var logoContainerMargin = 16;
var linkContainerWidth = 20;
var linkContainerMargin = 28;
var TableHeader = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  margin-top: 24px;\n  padding: 0px 16px;\n"], ["\n  display: flex;\n  margin-top: 24px;\n  padding: 0px 16px;\n"])));
var TableCol = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n\n  width: ", ";\n\n  text-align: ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n\n  width: ", ";\n\n  text-align: ", ";\n"])), function (props) {
    return props.contentWidth
        ? "calc(" + props.contentWidth + "px * " + props.weight + ")"
        : props.weight * 100 + "%";
}, function (props) {
    switch (props.orientation) {
        case "left":
        case "right":
            return props.orientation;
        default:
            return "left";
    }
});
var TableHeaderCol = styled(TableCol)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  &:first-child {\n    width: ", ";\n  }\n"], ["\n  &:first-child {\n    width: ", ";\n  }\n"])), function (props) {
    return props.contentWidth
        ? "calc((" + props.contentWidth + "px * " + props.weight + ")" + (props.hasLogo
            ? " + " + logoContainerWidth + "px + " + logoContainerMargin + "px"
            : "") + ")"
        : props.weight * 100 + "%";
});
var TableRow = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: flex;\n  flex-wrap: nowrap;\n  align-items: center;\n  padding: 8px 16px;\n\n  &:nth-child(odd){\n    background: #043F3D;\n  }\n\n  &:nth-child(even){\n    background: #0A3836;\n  }\n"], ["\n  display: flex;\n  flex-wrap: nowrap;\n  align-items: center;\n  padding: 8px 16px;\n\n  &:nth-child(odd){\n    background: #043F3D;\n  }\n\n  &:nth-child(even){\n    background: #0A3836;\n  }\n"])));
var LogoContainer = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: ", "px;\n  width: ", "px;\n  margin-right: ", "px;\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: ", "px;\n  width: ", "px;\n  margin-right: ", "px;\n"])), logoContainerWidth, logoContainerWidth, logoContainerMargin);
var ExternalLinkContainer = styled.div(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: ", "px;\n  height: ", "px;\n  margin: 0px 8px 0px ", "px;\n\n  svg {\n    opacity: 0.48;\n  }\n\n  &:hover {\n    svg {\n      opacity: 1;\n    }\n  }\n\n  @media (max-width: ", "px) {\n    margin: 0px 8px 0px 24px;\n  }\n"], ["\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: ", "px;\n  height: ", "px;\n  margin: 0px 8px 0px ", "px;\n\n  svg {\n    opacity: 0.48;\n  }\n\n  &:hover {\n    svg {\n      opacity: 1;\n    }\n  }\n\n  @media (max-width: ", "px) {\n    margin: 0px 8px 0px 24px;\n  }\n"])), linkContainerWidth, linkContainerWidth, linkContainerMargin - 8, sizes.xl);
var PaginationContainer = styled.div(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-top: 40px;\n"], ["\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin-top: 40px;\n"])));
var TableWithFixedHeader = function (_a) {
    var weights = _a.weights, labels = _a.labels, data = _a.data, orientations = _a.orientations, logos = _a.logos, externalLinks = _a.externalLinks, _b = _a.perPage, perPage = _b === void 0 ? 10 : _b, pageController = _a.pageController;
    var containerRef = useRef(null);
    var width = useElementSize(containerRef).width;
    var _c = useState(1), page = _c[0], _setPage = _c[1];
    var setPage = pageController ? pageController.setPage : _setPage;
    // Sync page controller with local state
    useEffect(function () {
        if (pageController && pageController.page !== page) {
            _setPage(pageController.page);
        }
    }, [page, pageController]);
    var availableContentWidth = useMemo(function () {
        var calculatedWidth = width - 32;
        if (logos) {
            calculatedWidth =
                calculatedWidth - logoContainerWidth - logoContainerMargin;
        }
        if (externalLinks) {
            calculatedWidth =
                calculatedWidth - linkContainerWidth - linkContainerMargin;
        }
        return calculatedWidth;
    }, [externalLinks, logos, width]);
    var pagination = useMemo(function () {
        if (data.length > 0) {
            return (_jsx(PaginationContainer, { children: _jsx(Pagination, { page: page, total: Math.ceil(data.length / perPage), setPage: setPage }, void 0) }, void 0));
        }
        return _jsx(_Fragment, {}, void 0);
    }, [data, page, perPage, setPage]);
    var paginatedData = useMemo(function () {
        return data.slice((page - 1) * perPage, page * perPage);
    }, [data, page, perPage]);
    return (_jsxs("div", __assign({ className: "d-flex flex-column", ref: containerRef }, { children: [_jsx(TableHeader, { children: weights.map(function (weight, index) { return (_jsx(TableHeaderCol, __assign({ weight: weight, contentWidth: availableContentWidth, orientation: orientations ? orientations[index] : undefined, hasLogo: Boolean(logos) }, { children: _jsx(SecondaryText, __assign({ color: "#ffffff" }, { children: labels[index] }), void 0) }), index)); }) }, void 0), paginatedData.length > 0 && _jsx(AnimatePresence, __assign({ initial: false, exitBeforeEnter: true }, { children: _jsx(motion.div, __assign({ transition: {
                        duration: 0.25,
                        type: "keyframes",
                        ease: "easeInOut",
                    }, initial: {
                        y: 50,
                        opacity: 0,
                    }, animate: {
                        y: 0,
                        opacity: 1,
                    }, exit: {
                        y: 50,
                        opacity: 0,
                    }, className: "w-100 mt-4", style: {
                        border: '1px solid rgba(226,248,241, 0.5)',
                    } }, { children: paginatedData.map(function (row, index) {
                        var rowNum = (page - 1) * perPage + index;
                        return (_jsxs(TableRow, { children: [logos && _jsx(LogoContainer, { children: logos[rowNum] }, void 0), row.map(function (col, colNum) { return (_jsx(TableCol, __assign({ weight: weights[colNum], contentWidth: availableContentWidth, orientation: orientations ? orientations[colNum] : undefined }, { children: col }), colNum)); }), externalLinks && (_jsx(BaseLink, __assign({ to: externalLinks[rowNum], target: "_blank", rel: "noreferrer noopener" }, { children: _jsx(ExternalLinkContainer, { children: _jsx(ExternalIcon, { color: "white" }, void 0) }, void 0) }), void 0))] }, index));
                    }) }), page) }), void 0), pagination] }), void 0));
};
export default TableWithFixedHeader;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7;
//# sourceMappingURL=TableWithFixedHeader.js.map